import { Gauge, FileSearch, MessagesSquare, Server, Globe } from "lucide-react";

export const products = [
  {
    slug: "website-performance-review",
    category: "Audits & Reviews",
    icon: Gauge,
    name: "Website Performance Review",
    cardDesc: "Identify performance bottlenecks, slow-loading resources, front-end inefficiencies, and opportunities to improve your website's loading experience.",
    price: 2999,
    priceLabel: "₹2,999",
    delivery: "2–4 business days",
    deliveryType: "Digital Report",
    cardCta: "View Product",
    eyebrow: "Performance Review",
    h1: "Understand why your website feels slow.",
    sub: "Bytevora reviews the technical factors affecting your website's loading performance and provides a prioritized plan for improving speed and efficiency.",
    meta: "Digital Report · 2–4 Business Days",
    ctaLabel: "Order Performance Review",
    positioning: "Why does my website feel slow?",
    overview: [
      "A slow website quietly costs you visitors, conversions, and credibility. This review identifies exactly where the performance problems are — and what to fix first.",
      "You receive a structured, prioritized report rather than a raw list of tool output, so every recommendation connects to a practical improvement.",
    ],
    included: [
      "Page-load analysis",
      "Core Web Vitals observations",
      "Image optimization review",
      "JavaScript and CSS observations",
      "Font-loading review",
      "Caching observations",
      "Server response observations",
      "Third-party script review",
      "Prioritized performance recommendations",
    ],
    receive: ["A digital performance report outlining key bottlenecks and practical recommendations ranked by priority."],
    need: ["Website URL", "Brief description of known performance issues", "Relevant access only where necessary"],
    notIncluded: ["Implementation of fixes", "Full website redesign", "Full security audit", "Full SEO audit", "Server migration", "Ongoing monitoring"],
    deliveryNote: "Estimated delivery within 2–4 business days after receiving the required website information.",
    scopeNote: "This product includes analysis and recommendations. Implementation of recommended fixes is not included unless separately agreed.",
  },
  {
    slug: "website-technical-audit",
    category: "Audits & Reviews",
    icon: FileSearch,
    name: "Website Technical Audit",
    cardDesc: "A structured technical review of your website covering performance, responsiveness, implementation quality, technical SEO fundamentals, security observations, and deployment setup.",
    price: 4999,
    priceLabel: "₹4,999",
    delivery: "3–5 business days",
    deliveryType: "Digital Report",
    cardCta: "View Product",
    eyebrow: "Website Technical Audit",
    h1: "Find what's holding your website back.",
    sub: "Get a structured technical assessment of your website with clear findings, prioritized recommendations, and practical next steps.",
    meta: "Digital Audit Report · 3–5 Business Days",
    ctaLabel: "Purchase Audit",
    positioning: "What is technically wrong with my website overall?",
    overview: [
      "Before investing in redevelopment or optimization, it helps to know exactly what needs attention. This audit gives you a complete technical picture of your existing website.",
      "Good fit for businesses with an existing website that want to understand what should be improved before committing to larger work.",
    ],
    included: [
      "Website performance review",
      "Mobile responsiveness review",
      "Front-end implementation review",
      "Technical SEO fundamentals",
      "Page structure review",
      "Accessibility observations",
      "HTTPS and basic security observations",
      "Broken links and implementation issues",
      "Hosting and deployment observations",
      "General usability observations",
      "Prioritized recommendations",
    ],
    receive: ["Executive summary", "Technical findings", "Priority classification", "Recommended fixes", "Performance observations", "Implementation recommendations", "Suggested next steps"],
    need: ["Website URL", "Brief description of known issues", "Relevant access only where necessary"],
    notIncluded: ["Implementation of recommended fixes", "Content writing", "Complete security certification", "Ongoing monitoring"],
    deliveryNote: "Estimated delivery within 3–5 business days after receiving the required website details.",
    scopeNote: "This product includes analysis and recommendations. Implementation work is not included unless separately agreed.",
  },
  {
    slug: "technology-consultation",
    category: "Consulting",
    icon: MessagesSquare,
    name: "Technology Consultation",
    cardDesc: "A focused one-on-one consultation for businesses or founders who need help making technical decisions before starting, improving, or scaling a digital product.",
    price: 2499,
    priceLabel: "₹2,499",
    delivery: "Scheduled online session",
    deliveryType: "Online Consultation",
    meta2: "60-minute session",
    cardCta: "Book Consultation",
    eyebrow: "Technology Consultation",
    h1: "Make the technical decision before making the expensive one.",
    sub: "Discuss your product idea, architecture, infrastructure, software challenge, or technology roadmap with Bytevora and get practical direction for your next step.",
    meta: "60-Minute Online Consultation",
    ctaLabel: "Book Consultation",
    positioning: "Get focused technical guidance before committing budget.",
    overview: [
      "A single well-made technical decision can save weeks of rework. This session is built for founders and teams who want clear, honest technical direction before they commit.",
      "Booking details and available scheduling options will be provided during or immediately after the purchase process.",
    ],
    included: [],
    topics: ["Product architecture", "Technology selection", "SaaS planning", "Web or mobile development", "API strategy", "Existing system improvement", "Cloud infrastructure", "Deployment strategy", "Automation opportunities", "AI integration", "Scalability planning", "Technical feasibility"],
    receive: ["60-minute online consultation", "Focused discussion around your technical challenge", "Practical recommendations", "Follow-up summary where included"],
    need: ["A short description of the challenge or decision you want to discuss", "Any relevant links, documents, or context shared in advance"],
    notIncluded: ["Development or implementation", "Formal legal or security certification", "Complete architecture documentation unless separately agreed"],
    deliveryNote: "The session is scheduled after your booking is confirmed. You will receive scheduling instructions by email.",
    scopeNote: "The consultation does not include development, formal legal or security certification, complete architecture documentation, or implementation unless separately agreed.",
  },
  {
    slug: "cloud-deployment-setup",
    category: "Implementation Packages",
    icon: Server,
    name: "Cloud Deployment Setup",
    cardDesc: "Get an existing supported application configured and deployed to a production-ready server or cloud environment.",
    price: 9999,
    priceLabel: "₹9,999",
    delivery: "2–5 business days",
    deliveryType: "Digital / Infrastructure Setup",
    cardCta: "View Setup",
    eyebrow: "Cloud Deployment",
    h1: "Take your application from code to production.",
    sub: "Bytevora configures and deploys your existing supported application to an agreed server or cloud environment with a structured production setup.",
    meta: "Digital Setup · 2–5 Business Days",
    ctaLabel: "Purchase Deployment Setup",
    positioning: "One application. One environment. Production-ready.",
    overview: [
      "Deploying to production involves far more than uploading code. This package gives your application a properly configured, secure, production-grade home.",
      "The package covers one supported application, one production environment, and one server or cloud instance — for example Django, Laravel, Node.js, Next.js, React, or PHP applications.",
    ],
    included: [
      "Server environment setup",
      "Application deployment",
      "Domain configuration",
      "SSL installation",
      "Web-server configuration",
      "Environment-variable setup",
      "Application process configuration",
      "Basic firewall configuration",
      "Production database connection",
      "Basic deployment documentation",
    ],
    receive: ["A deployed production application on your server or cloud environment", "Basic deployment documentation for future reference"],
    need: ["Application source code or repository access", "Server or cloud access", "Domain access where required", "Environment configuration details", "Database details", "Required application credentials"],
    notIncluded: ["Application development", "Major bug fixing", "Cloud-provider fees", "Domain fees", "Paid software licences", "Application redesign", "Ongoing server management", "Advanced DevOps architecture", "Multi-server or Kubernetes environments"],
    deliveryNote: "Estimated delivery within 2–5 business days. The timeline begins after all required access and project information are received.",
    scopeNote: "Covers one supported application, one production environment, and one server or cloud instance. Larger requirements are quoted as a custom engagement.",
    secondaryCta: { label: "Need Advanced Infrastructure?", to: "/contact" },
  },
  {
    slug: "business-website-launch",
    category: "Implementation Packages",
    icon: Globe,
    name: "Business Website Launch Package",
    cardDesc: "A structured website package for businesses that need a professional, responsive, production-ready online presence.",
    price: 24999,
    priceLabel: "₹24,999",
    delivery: "Approx. 10–15 business days",
    deliveryType: "Digital Project Delivery",
    cardCta: "Explore Package",
    eyebrow: "Business Website",
    h1: "A professional website built to launch your business online.",
    sub: "Bytevora designs and develops a modern business website with responsive layouts, essential pages, enquiry functionality, foundational SEO setup, and production deployment.",
    meta: "Digital Project · Approx. 10–15 Business Days",
    ctaLabel: "Purchase Website Package",
    positioning: "Everything a business needs to launch properly.",
    overview: [
      "This package is designed for businesses that need a credible, professional online presence without an open-ended project scope.",
      "You get a defined set of pages, a working enquiry path, foundational technical SEO, and a deployed production website — with the source code handed over to you.",
    ],
    included: [
      "Up to 5 core pages",
      "Responsive design and development",
      "Home page",
      "About page",
      "Services page",
      "Contact page",
      "One additional standard page",
      "Contact / enquiry form",
      "Basic technical SEO setup",
      "Metadata setup",
      "Mobile optimization",
      "Deployment",
      "SSL setup",
      "Source-code handover",
    ],
    addons: ["Additional pages", "Blog", "CMS", "Admin panel", "Booking system", "Payment integration", "CRM integration", "Multilingual functionality", "Advanced animations", "API integrations", "E-commerce functionality"],
    need: ["Business details", "Logo / branding", "Page content", "Images or media", "Contact details", "Domain and hosting information where applicable"],
    notIncluded: ["Content writing beyond supplied material", "E-commerce functionality", "Custom web applications", "Ongoing maintenance unless separately agreed", "Items listed as optional add-ons"],
    deliveryNote: "Approx. 10–15 business days. The estimated timeline begins after required content, approvals, and project information are received.",
    scopeNote: "Requests outside the defined package may require a separate quotation or change request.",
    secondaryCta: { label: "Need a Custom Website?", to: "/contact" },
  },
];

export const categories = ["All Products", "Audits & Reviews", "Implementation Packages", "Consulting"];

export const purchaseSteps = [
  { num: "01", title: "Choose a Product", desc: "Select the product or package that matches what you need." },
  { num: "02", title: "Review the Scope", desc: "Check what is included, what is excluded, delivery expectations, and any information we will need from you." },
  { num: "03", title: "Complete Payment", desc: "Submit the required billing details and complete payment through the available payment method." },
  { num: "04", title: "Receive Next Steps", desc: "After payment, you will receive confirmation and instructions for providing the information or access required for delivery." },
];

export const whyBuy = [
  { title: "Transparent Scope", desc: "Each product explains what is included before purchase." },
  { title: "Defined Delivery", desc: "Delivery format and expected timeline are stated clearly." },
  { title: "Practical Outcomes", desc: "Products are structured around a specific technical result rather than vague service promises." },
  { title: "Professional Support", desc: "If your requirement falls outside the defined scope, Bytevora can recommend a custom engagement instead." },
];

export function getProduct(slug) {
  return products.find((p) => p.slug === slug);
}
